import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import MDButton from "components/MDButton";

/**
 * Reusable confirmation dialog for any "translate to all languages"
 * action. Owns the in-flight state, surfaces the overwrite warning,
 * runs the caller-supplied async work, and renders a result summary
 * keyed by language code.
 *
 * Designed so the same component drives both the "translate one
 * question" and the "translate a whole title + its questions" flows —
 * the parent just passes a different `onConfirm` and `summaryRenderer`.
 *
 * Props
 *   open               — controlled visibility
 *   onClose            — called when the user closes / cancels
 *   onConfirm          — async fn that runs the actual translation;
 *                        receives `{ setStatus }` and should return
 *                        the raw API response on success or throw / return
 *                        `{ status >= 400, message }` on failure
 *   title              — dialog heading (e.g. "Translate question")
 *   description        — short explanation rendered above the warning
 *   targetLangsLabel   — human-readable list of languages we'll
 *                        translate into (e.g. "English, Portuguese, …")
 *   itemSummaryLabel   — shown alongside the warning, e.g.
 *                        "1 question" or "12 questions across this title"
 *   summaryRenderer    — optional fn(responseData) => JSX rendered in
 *                        the success state below the chip row
 *   confirmLabel       — button label (default "Translate now")
 */
function TranslateConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  targetLangsLabel,
  itemSummaryLabel,
  summaryRenderer,
  confirmLabel,
}) {
  const theme = useTheme();
  const [stage, setStage] = useState("idle"); // idle | running | success | error
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [response, setResponse] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStage("idle");
    setStatusMsg("");
    setErrorMsg("");
    setResponse(null);
  }, [open]);

  const isRunning = stage === "running";
  const isDone = stage === "success" || stage === "error";

  const handleClose = () => {
    if (isRunning) return; // don't allow mid-flight close
    onClose?.();
  };

  const handleRun = async () => {
    setStage("running");
    setStatusMsg("Sending content to the translator…");
    setErrorMsg("");
    setResponse(null);
    try {
      const res = await onConfirm({ setStatus: setStatusMsg });
      const status = typeof res?.status === "number" ? res.status : 200;
      if (status >= 400) {
        setStage("error");
        setErrorMsg(
          res?.message || "Translation failed. Try again in a moment.",
        );
        return;
      }
      setResponse(res || {});
      setStage("success");
    } catch (e) {
      console.error("[TranslateConfirmDialog]", e);
      setStage("error");
      setErrorMsg(e?.message || "Translation failed. Try again in a moment.");
    }
  };

  const summary = useMemo(
    () => (response && summaryRenderer ? summaryRenderer(response) : null),
    [response, summaryRenderer],
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: "background.default" } }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <TranslateRoundedIcon sx={{ color: "primary.main" }} />
          <span>{title}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        {/* Idle / setup screen */}
        {stage === "idle" && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.primary">
              {description}
            </Typography>

            {itemSummaryLabel && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={itemSummaryLabel}
                sx={{ alignSelf: "flex-start" }}
              />
            )}

            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: 1,
                borderColor: alpha(theme.palette.warning.main, 0.4),
                bgcolor: alpha(theme.palette.warning.main, 0.08),
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ mb: 0.5 }}
              >
                <WarningAmberRoundedIcon
                  fontSize="small"
                  sx={{ color: "warning.main", mt: "2px" }}
                />
                <Typography variant="body2" color="text.primary">
                  <strong>Existing translations will be overwritten.</strong>{" "}
                  Running this action regenerates every translated row from
                  the current source content. Manual edits in other languages
                  will be replaced.
                </Typography>
              </Stack>
              {targetLangsLabel && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5, pl: 3.5 }}
                >
                  Target languages: {targetLangsLabel}
                </Typography>
              )}
            </Box>
          </Stack>
        )}

        {/* Running */}
        {stage === "running" && (
          <Stack spacing={2} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={42} />
            <Typography variant="body2" color="text.primary">
              {statusMsg}
            </Typography>
            <LinearProgress sx={{ width: "100%" }} />
            <Typography variant="caption" color="text.secondary">
              {statusMsg.includes("slide")
                ? "Processing each subtitle slide one at a time. Large titles can take several minutes — keep this dialog open."
                : "Translating into all 32 app languages usually takes 30-120 seconds. Please keep this dialog open."}
            </Typography>
          </Stack>
        )}

        {/* Success */}
        {stage === "success" && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleRoundedIcon sx={{ color: "success.main" }} />
              <Typography variant="body1" color="text.primary">
                {response?.message || "Translation completed."}
              </Typography>
            </Stack>
            {summary}
          </Stack>
        )}

        {/* Error */}
        {stage === "error" && (
          <Alert severity="error" variant="outlined">
            {errorMsg}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {!isDone && (
          <Button onClick={handleClose} disabled={isRunning} color="inherit">
            Cancel
          </Button>
        )}
        {stage === "idle" && (
          <MDButton
            color="primary"
            variant="gradient"
            onClick={handleRun}
            startIcon={<TranslateRoundedIcon />}
          >
            {confirmLabel || "Translate now"}
          </MDButton>
        )}
        {stage === "error" && (
          <MDButton color="primary" variant="gradient" onClick={handleRun}>
            Try again
          </MDButton>
        )}
        {stage === "success" && (
          <MDButton color="success" onClick={onClose}>
            Done
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

TranslateConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  targetLangsLabel: PropTypes.string,
  itemSummaryLabel: PropTypes.string,
  summaryRenderer: PropTypes.func,
  confirmLabel: PropTypes.string,
};

TranslateConfirmDialog.defaultProps = {
  description: "",
  targetLangsLabel: "",
  itemSummaryLabel: "",
  summaryRenderer: undefined,
  confirmLabel: "Translate now",
};

export default TranslateConfirmDialog;
